import React, { useEffect } from 'react';
import { ModalProvider } from '@/context/ModalContext';
import { AuthProvider } from '@/context/AuthContext';
import { Auth } from '@/components/Auth';
import { ModalStack } from '@/components/ModalStack';
import { ConfigProvider } from '@/context/ConfigContext';
import { MuiThemeProvider } from '@/components/MuiThemeProvider';
import { publicRuntimeConfig } from '@repo/config';
import {
  AnalyticClient,
  AppsflyerClient,
  OneSignalClient,
  ALLOW_ANALYTIC_ALL_ROUTES_PATH,
  LandingUrl,
  LandingHomeWithLangUrl,
  getCookieValue,
} from '@repo/utils';

import '@/i18n';
import { useTranslation } from './hooks/useTranslation';
import { I18N_COOKIE_NAME, DEFAULT_LANG } from '@/i18n';

interface Props {
  defaultOpen?: boolean;
}

const App: React.FC<Props> = ({ defaultOpen }) => {
  const { i18n } = useTranslation();

  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath !== LandingUrl) {
      return;
    }
    const cookieLanguage = getCookieValue(I18N_COOKIE_NAME);
    if (!cookieLanguage || cookieLanguage === DEFAULT_LANG) {
      return;
    }
    if (publicRuntimeConfig.languages.landingList.includes(cookieLanguage)) {
      location.replace(LandingHomeWithLangUrl(cookieLanguage));
    }
  }, []);

  useEffect(() => {
    window.AuthModule = window.AuthModule || {};
    window.AuthModule.AnalyticClient = AnalyticClient;
    window.AuthModule.AppsflyerClient = AppsflyerClient;

    AnalyticClient.init({ user: null, language: i18n.language, whitelistRoutes: [ALLOW_ANALYTIC_ALL_ROUTES_PATH] });

    if (OneSignalClient.isPushApiEnabled() && OneSignalClient.isPushApiSupported()) {
      OneSignalClient.showPushApiPrompt();
    }
  }, []);

  return (
    <MuiThemeProvider>
      <ConfigProvider>
        <ModalProvider>
          <AuthProvider defaultOpen={defaultOpen}>
            <Auth />
            <ModalStack />
          </AuthProvider>
        </ModalProvider>
      </ConfigProvider>
    </MuiThemeProvider>
  );
};

export default React.memo(App);
